import React, { useState, useEffect, useReducer, useImperativeHandle, forwardRef } from "react";
import MetricRadioGroup from "./MetricRadioGroup";
import MetricCheckboxGroup from "./MetricCheckboxGroup";
import MetricConsiderProgress from "./MetricProgressConsider";
import SearchBoxComp from "../../../inputs/searchbox.component";
import { TextareaFieldComp } from "../../../inputs/fields.component";
import MetricInstanceSelection from "./MetricInstanceSelection";
import GoalServices from "../../../../services/goal.services";
import MetricPermissions from "./MetricPermissions";
import SingleSelectSearch from "../../../inputs/SingleSelectSearch";
import MetricSubheader from "./MetricSubheader";
import ADOItemsDropdown from "./ADOItemsDropdown";
import ADOWorkItemsList from "./ADOWorkItemsList";
import { reactLoader, removeReactLoader, constructNestedKRPayload } from "../../../../utils/common";
import KrAlignDependance from "./krAlignDependence"; //Warning: There are multiple modules with names that only differ in casing.
import NestedInlineComp from "./NestedInline.component";
import AddKrTag from "../../../../krTags/components/addkrTag.component";    
import { useTranslation } from 'react-i18next';
import RetainUpdateModal from '../../../retainUpdateModal.component';
import KrRelationshipWrapper from "./krRelationshipWrapper";
import { AddFinancialTargetsButton } from '../AddFinancialTargetsButton';

const AdoPostLogin = forwardRef((props, ref) => {
  const { t } = useTranslation()
  const [showSavePopup, setShowSavePopup] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const [radioSelector, setRadioSelector] = useState("id");
  const [instances, setInstances] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [filterSelections, setFilterSelections] = useState("");
  const [calculationType, setCalculationType] = useState("level_0");
  const [itemStates, setItemStates] = useState([]);
  const [doneMetrics, setDoneMetrics] = useState(new Set());
  const [progress, setProgress] = useState("percentage");
  const [considerProgress, setConsiderProgress] = useState("1");
  const [dependenciesData, setDependenciesData] = useState(
    props.item && props.item.kr_dependent !== undefined &&
      props.item.kr_dependent.length > 0 ?
      props.item.kr_dependent : []
  );
  const [tags,setTags]=useState([]);
  const [alignData, setAlignData] = useState(
    props.item && props.item.kr_aligns !== undefined && 
    props.item.kr_aligns.length > 0 ? 
      props.item.kr_aligns : []
  );
  const [teamPermissions, setTeamPermissions] = useState(
    props.item && props.item.permission_teams ? 
      props.item.permission_teams : []
  );
  const [permissions, setPermissions] = useState(
    props.item && props.item.permission ?
      props.item.permission.split(',').map(Number) : 
      props.goalDetails.type == 1 ? [8] : [10]
  );

  const initialErrors = {
    instances: "",
    projects: "",
    filterSelections: "",
    doneMetrics: ""
  };

  const errorReducer = (state, action) => {
    return { ...state, ...{ [action.type]: action.message } };
  };

  const [error, errorDispatch] = useReducer(errorReducer, initialErrors);

  const saveKr = (callback = null) => {
    handleSave(true, callback);
  }

  const handleSaveCompleted = (callback = null, data = {}) => {
    if (callback && typeof callback === 'function') {
      callback(data);
    } else {
      props.closeKrRightPane(true)
    }
  }

  useEffect(() => {
    findAndSetInstances();
  }, []);
  
  useImperativeHandle(ref, () => ({
    handleCancel,
    handleSave
  }));
  /**
   * Gets all instances and sets a default selected instance
   */
  const findAndSetInstances = () => {
    const goalService = new GoalServices();
    reactLoader();

    let selectedInstance;

    goalService
      .getAllInstances({
        app: "azure_cloud"
      })
      .then(res => {
        removeReactLoader();
        if (res && res.data && res.data.data) {
          const instances = res.data.data.reverse();
          const formattedInstances = formatInstancesForDropdown(instances);

          if (
            props.item &&
            props.item.metricData &&
            props.item.metricData.organization
          ) {
            const orgName = props.item.metricData.organization;
            selectedInstance = formattedInstances.find(
              inst => inst.name === orgName
            );

            if (!selectedInstance) {
              selectedInstance = {
                name: orgName,
                token_state: "invalid",
                value: formattedInstances.length
              };
              formattedInstances.push(selectedInstance);
            }

            // Set other pieces of state as well
            setConsiderProgress(props.item.progress_consider);
          } else {
            selectedInstance = formattedInstances[0];
          }

          let message = "";
          if (!formattedInstances.length) {
            message =
              t("The associated Azure DevOps account has no organizations.");
          }

          setInstances(formattedInstances);
          setSelectedInstance(selectedInstance);
          errorDispatch({
            type: "instances",
            message
          });
        } else {
          errorDispatch({
            type: "instances",
            message:
              t("Unable to retrieve Azure DevOps organizations at this moment.")
          });
        }
      })
      .catch(e => {
        console.log(e);
        removeReactLoader();
      });
  };

  useEffect(() => {
    if (!selectedInstance) return;
    setProjects([]);
    setSelectedProject(null);
    reactLoader();

    const goalService = new GoalServices();

    goalService
      .getExtProjects({
        app: "azure_cloud",
        instance_url: selectedInstance.name
      })
      .then(res => {
        removeReactLoader();
        if (res && res.data && res.data.data) {
          const projects = res.data.data.value.reverse();
          const formattedProjects = formatProjectsForDropdown(projects);
          let selectedProject = null;

          if (
            props.item &&
            props.item.metricData &&
            props.item.metricData.project &&
            props.item.metricData.organization === selectedInstance.name
          ) {
            const project = props.item.metricData.project;
            selectedProject = formattedProjects.find(
              proj => proj.name === project
            );

            if (!selectedProject) {
              selectedProject = {
                name: project,
                state: "invalid",
                value: formattedProjects.length
              };
              formattedProjects.push(selectedProject);
            }
          }

          let message = "";
          if (!formattedProjects.length) {
            message = t("The selected organization has no projects.");
          }

          setSelectedProject(selectedProject);
          setProjects(formattedProjects);
          errorDispatch({
            type: "projects",
            message
          });
        } else {
          errorDispatch({
            type: "projects",
            message:
              t("Unable to retrieve projects from this organization at this moment.")
          });
        }
      })
      .catch(e => {
        console.log(e);
        errorDispatch({
          type: "projects",
          message:
            `${t('Unable to retrieve projects from this organization at this')} \
            ${t('moment.')} ${t('If you authenticated through a personal access token,')} \
            ${t('ensure that the token has not expired.')}`
        });
        removeReactLoader();
      });
  }, [selectedInstance]);

  /***
   * Fetches item states on project change
   */
  useEffect(() => {
    if (!selectedProject) return;
    reactLoader();
    const goalService = new GoalServices();

    goalService
      .fetchAzureStates({
        instance_url: selectedInstance.name,
        project: selectedProject.name
      })
      .then(res => {
        removeReactLoader();
        const formattedStates = res.data.states.map(state => {
          return { id: state, label: state };
        });

        const doneMetrics = new Set();

        if (
          props.item &&
          props.item.metricData &&
          props.item.metricData.states &&
          props.item.metricData.organization === selectedInstance.name &&
          props.item.metricData.project === selectedProject.name
        ) {
          const states = props.item.metricData.states;
          states.forEach(state => {
            if (formattedStates.find(st => st.id === state)) {
              doneMetrics.add(state);
            }
          });
          setCalculationType(props.item.metricData.calculation_type);
          setProgress(props.item.metricData.progress);
          setRadioSelector(props.item.metricData.item_type);

          switch (props.item.metricData.item_type) {
            case "id":
              setFilterSelections(
                Object.keys(props.item.metricData.items).join(",")
              );
              break;
            case "name":
              setFilterSelections(props.item.metricData.items);
              break;
            case "query":
              setFilterSelections(
                Object.values(props.item.metricData.items)[0]
              );
              break;
            default:
              break;
          }
        } else {
          setRadioSelectorAndResetFilter("id");
          setCalculationType("level_0");
          setProgress("percentage");
        }

        setDoneMetrics(doneMetrics);
        setItemStates(formattedStates);
      })
      .catch(e => {
        console.log(e);
        removeReactLoader();
      });
  }, [selectedProject]);

  useEffect(() => {
    if (error.filterSelections) {
      errorDispatch({ type: "filterSelections", message: "" });
    }
  }, [filterSelections]);

  /**
   * Sets the radio selector and resets the filter on switch
   * @param {string} id
   */
  const setRadioSelectorAndResetFilter = id => {
    switch (id) {
      case "id":
        setFilterSelections("");
        break;
      case "name":
        setFilterSelections({});
        break;
      case "query":
        setFilterSelections(null);
        break;
      default:
        setFilterSelections("");
        break;
    }
    setRadioSelector(id);
  };

  /**
   * When the user connects to another basic auth, it adds it to the current list.
   * If they decide on OAuth, it resets to default instance.
   * @param {string} orgName
   * @param {string} authType
   */
  const updateInstances = (orgName, authType) => {
    if (authType === "bearer") {
      findAndSetInstances();
    } else {
      const instance = formatInstancesForDropdown(
        [{ AccountName: orgName }],
        instances.length
      );
      const allInstances = instances.concat(instance);
      setInstances(allInstances);
      setSelectedInstance(allInstances[allInstances.length - 1]);
    }
  };

  /**
   * Adds and removes from done metrics set
   * @param {Event} event
   */
  const setDoneMetricsHandler = event => {
    const clonedSet = new Set(doneMetrics);
    if (event.currentTarget.checked) {
      clonedSet.add(event.currentTarget.id);
    } else {
      clonedSet.delete(event.currentTarget.id);
    }
    setDoneMetrics(clonedSet);
  };

  const handleCancel = () => {
    props.closeKrRightPane(false,props.item.id ? props.item.id : '');
  }

  const saveTag = (childData) =>{
    setTags(childData)
  }

  const handleSave = async (bypassConfirmationPopup = false, callback = null) => {
    if (!checkIfParamsAreSet()) {
      return;
    }

    const params = await validateSelections();
    if (params) {
      if (!bypassConfirmationPopup
        && props.lastType != props.krType
        && props.lastType !== 'krstudio' // no alert when going from krstudio to person, ww-11221
        && (props.lastType == 'kpi' || props.lastType == 'person')
        && props.addEditFlag) {
        setShowSavePopup(true);
        return;
      }

      reactLoader();
      const goalService = new GoalServices();
      if (selectedType !== null) {
        params.retainUpdates = selectedType;
      }

      if(props.isChildKr && props.fromParentKr){
        //params.ownerName = this.state.data_provider.name;
        params.duration = props.childKrDuration;
        params.real_owner_fullname = props.showRealOwner ? props.realOwner.name : "";
        params.real_owner_pic = props.showRealOwner ? props.realOwner.avatar : "";
        if(props.item.parent){
          // params.start_date = this.props.childKrDuration.start ;
          // params.end_date = this.props.childKrDuration.end;
          params.parent = props.item.parent;
        }
        //params.duration = this.state.data_provider.name;
        props.saveNestedKr(params, props.nestedKrIndex, props.nestedKrAction);
        removeReactLoader();
      } else if(props.mitChildKr){
				params.duration = props.childKrDuration;
				params.real_owner_fullname = props.showRealOwner ? props.realOwner.name : "";
				params.real_owner_picture = props.showRealOwner ? props.realOwner.avatar : "";
				params.real_owner_pic = props.showRealOwner ? props.realOwner.avatar : "";
        params.end_date = props.item.end_date;
        params.start_date = props.item.start_date;
				props.saveQuarterlyKR(params, props.mitIndex);
        this.handleSaveCompleted(callback, params);
				// props.closeKrRightPane();
			}else {
        if(props.item.parent){
          params.start_date = props.childKrDuration.start ;
          params.end_date = props.childKrDuration.end;
          params.parent = props.item.parent;
          params.clearCache = true;
        }
        goalService
          .addEditKr({
            goalId: props.goalDetails.id,
            metric: params
          })
          .then(res => {
            if (res) {
              if (res.data.data) {
                if (!props.onlyMetEdit) {
                  if (res.data.data.parent) {
                    props.actions.addUpdateNestedKr(res.data.data)
                  } else {
                    props.actions.addNewKr(res.data.data);
                  }
                }
                // goalService.triggerGoalMetricDetails({
                //   goalId: props.goalDetails.id
                // });
                // props.closeKrRightPane(true);
                handleSaveCompleted(callback, res.data.data);
              }
            }
            removeReactLoader();
          });
      }
    }
  };

  /**
   * Validates the user input selections based on the radio selector
   * @returns {mixed}
   */
  const validateSelections = () => {
    switch (radioSelector) {
      case "id":
        const ids = [];
        const selections = filterSelections.split(",");
        for (let i = 0; i < selections.length; i++) {
          const selection = selections[i].trim();
          if (selection.startsWith('http')) {
            const re = /(\d{1,})$/;
            const url = selection.replace(/\//g, "");
            const match = re.exec(url);
            if (match) {
              ids.push(match[0]);
            } else {
              errorDispatch({
                type: "filterSelections",
                message:
                  `${t('Enter valid numerical IDs or Work Item urls with a')} \
                  ${t('single comma delimiter.')}`
              });
              return false;
            }
          } else if (!isNaN(parseInt(selection))) {
            ids.push(selection);
          } else {
            errorDispatch({
              type: "filterSelections",
              message:
                `${t('Enter valid numerical IDs or Work Item urls with')} \
                ${t('a single comma delimiter.')}`
            });
            return false;
          }
        }

        const goalService = new GoalServices();
        return goalService
          .validateSelections("azure_cloud", radioSelector, {
            instance_url: selectedInstance.name,
            project: selectedProject.name,
            ids: ids.join(",")
          })
          .then(res => {
            if (res && res.data) {
              if (res.data.status === "success") {
                const badIds = [];
                res.data.work_items.value.forEach(workItem => {
                  if (workItem.fields["System.TeamProject"] !== selectedProject.name) {
                    badIds.push(workItem.id);
                  }
                })

                if (badIds.length) {
                  const singular = badIds.length === 1;
                  errorDispatch({
                    type: "filterSelections",
                    message: `Work Item${ singular ? "" : "s"} \
                      ${badIds.join(", ")} ${singular ? "is" : "are"} outside \
                      the scope of the selected project.`
                  })
                  return false;
                } else {
                  errorDispatch({
                    type: "filterSelections",
                    message: ""
                  });
                  return constructPayload(res.data.work_items);
                }
              } else {
                errorDispatch({
                  type: "filterSelections",
                  message:
                    `${t('One or more IDs do not exist or are outside')} \
              ${t('the scope of the selected project.')}`
                });
                return false;
              }
            } else return false;
          });
      case "name":
      case "query":
        return constructPayload(filterSelections);
      default:
        return false;
    }
  };

  /**
   * Basic error checking to see if all required inputs are filled
   * @returns {boolean}
   */
  const checkIfParamsAreSet = () => {
    let noErrors = true;
    const metricName = $("#metric-name-text").val();
    if (
      typeof metricName === "undefined" ||
      metricName == null ||
      metricName == ""
    ) {
      noErrors = false;
      props.showMetricNameErr();
    }

    if (doneMetrics.size === 0) {
      noErrors = false;
      errorDispatch({
        type: "doneMetrics",
        message: t("At least one category is required.")
      });
    }
    switch (radioSelector) {
      case "id":
        if (!filterSelections.trim().length) {
          errorDispatch({
            type: "filterSelections",
            message:
              (`Enter valid numerical IDs or the urls of your Work Items \
              with a single comma delimiter.`)
          });
        }
        break;
      case "name":
        if (!Object.keys(filterSelections).length) {
          errorDispatch({
            type: "filterSelections",
            message: t("Choose at least one Work Item.")
          });
          noErrors = false;
        }
        break;
      case "query":
        if (!filterSelections) {
          errorDispatch({
            type: "filterSelections",
            message: t("Choose at least one query.")
          });
          noErrors = false;
        }
        break;
      default:
        break;
    }
    return noErrors;
  };

  /**
   * Creates the payload for backend to save the key result
   * @param {mixed} workItems
   * @returns {object}
   */
  const constructPayload = workItems => {
    let items = {};
    switch (radioSelector) {
      case "id":
        workItems.value.forEach(item => {
          items[item.id] = {
            id: item.id,
            name: item.fields["System.Title"]
          };
        });
        break;
      case "name":
        for (let itemId in workItems) {
          items[itemId] = {
            id: itemId,
            name: workItems[itemId].name
          };
        }
        break;
      case "query":
        items[workItems.id] = {
          id: workItems.id,
          name: workItems.name,
          wiql: workItems.wiql
        };
        break;
      default:
        break;
    }

    const params = {};

    params.source = "8";
    params.real_owner = props.realOwner.id;
    params.ext_app_id = "13";
    params.ext_source_name = $("#metric-name-text").val();
    
    if(props.isChildKr && props.fromParentKr && document.querySelectorAll('textarea#metric-name-text').length > 1){
      params.ext_source_name = document.querySelectorAll('textarea#metric-name-text')[1].value;
    }
    params.description = props.krNote??'';
    params.progress_consider = considerProgress;
    params.instance_url = selectedInstance.name;
    params.ext_source_data = {
      organization: selectedInstance.name,
      project: selectedProject.name,
      items: items,
      item_type: radioSelector,
      states: [...doneMetrics],
      progress: progress,
      calculation_type: radioSelector === "query" ? "level_0" : calculationType
    };

    if (props.goalDetails && props.goalDetails.all_metrics == 1) {
      params.permissions = props.goalDetails.permission;
      params.permission_teams = props.goalDetails.permission_teams;
    } else {
      params.permissions = permissions.toString();
      params.permission_teams = teamPermissions.map(team => team.id).toString();
    }
    if (props.item && props.item.id) {
      params.id = props.item.id;
    }
    params.dependentKr = dependenciesData.map(item => item.id);
    params.krAligns = alignData.map(item => item.id);
    params.krTagIds = tags.length ? tags : [''];
    params["nested_krs"] = constructNestedKRPayload(props);
    params.isQuarter  = props.isQuarter;
    params.kr_duration = props.kr_duration;
    return params;
  };

  /**
   * Adds name and value keys to array of objects for the dropdown menu
   * @param {array} instances
   * @param {integer} value
   * @returns {array}
   */
  const formatInstancesForDropdown = (instances, value = undefined) => {
    return instances.map((instance, i) => {
      return { ...instance, name: instance.AccountName, value: value || i };
    });
  };

  /**
   * Adds value key to array of objects for the dropdown menu
   * @param {array} projects
   * @returns {array}
   */
  const formatProjectsForDropdown = projects => {
    return projects.map((project, i) => {
      return { ...project, value: i };
    });
  };

  /**
   * Adds selected item to the list of selected items
   * @param {object} item
   */
  const addWorkItemByName = item => {
    setFilterSelections({
      ...filterSelections,
      ...{ [item.id]: item }
    });
  };

  /**
   * Removes selected item from the list of selected items
   * @param {object} item
   */
  const deleteWorkItemByName = item => {
    const newFilter = { ...filterSelections };
    delete newFilter[item.id];
    setFilterSelections(newFilter);
  };

  /**
   *
   * @param {array} items
   * @param {object} inst
   * @returns {React Component}
   */
  const renderDropdownMenu = (items, inst) => {
    const filteredItems = items.map(item => {
      return {
        id: item.fields["system.id"],
        name: item.fields["system.title"]
      };
    });

    const checkedList = new Set(Object.keys(filterSelections));
    return (
      <ADOItemsDropdown
        items={filteredItems}
        checkedList={checkedList}
        deleteItem={deleteWorkItemByName}
        addItem={addWorkItemByName}
      />
    );
  };

  /**
   * Queries ADO for list of queries based on input
   * @param {array} text
   */
  const filterQueries = text => {
    if (!selectedInstance || !selectedProject) return [];
    const goalService = new GoalServices();
    return goalService
      .fetchAndFilterAzureQueries({
        instance_url: selectedInstance.name,
        project: selectedProject.name,
        term: text
      })
      .then(res => {
        if (res && res.data && res.data.data) {
          return res.data.data;
        } else return [];
      });
  };

  return (
    <div className="metric-submenu">
      {selectedInstance && (
        <div className="metric-submenu-section">
          <MetricSubheader
            title={t("Azure DevOps (ADO) organization")}
            required={true}
          />
          <MetricInstanceSelection
            instances={instances}
            selectedInstance={selectedInstance}
            handleChange={(e, item) => setSelectedInstance(item)}
            appName="Azure DevOps"
            authType={props.authType}
            updateInstances={updateInstances}
          />
        </div>
      )}

      {!!projects.length && (
        <div className="metric-submenu-section">
          <MetricSubheader title="Project" required={true} />
          <SingleSelectSearch
            placeholder={t("Search and select a project")}
            defaultSearchText={selectedProject ? selectedProject.name : ""}
            items={projects}
            minSearchLength={1}
            updateParentState={setSelectedProject}
            scrollToTop=".krRightPaneContent"
          />
        </div>
      )}

      {selectedProject && !error.projects && (
        <>
          <div className="metric-submenu-section">
            <MetricSubheader
              title={t("How do you want to get issue completion data from ADO?")}
              required={true}
            />
            <MetricRadioGroup
              radioSelector={radioSelector}
              setRadioSelector={setRadioSelectorAndResetFilter}
              items={[
                { id: "id", label: t("By Work Item ID") },
                { id: "name", label: t("By Work Item Name") },
                { id: "query", label: t("By Saved Query") }
              ]}
              radioGroupName="SetFirst"
              subclassDefined="radioMarginClass"
            />
          </div>

          {radioSelector === "id" && (
            <div className="metric-submenu-section">
              <MetricSubheader title="Work Item Id(s)" required={true} />
              <TextareaFieldComp
                textAreaWrapper="no-min-height"
                width="width70"
                textareaProps={{
                  value: filterSelections,
                  onChange: e => setFilterSelections(e.currentTarget.value),
                  placeholder: "Ex: 567, 12, 122",
                  rows: "1"
                }}
              />
              {error.filterSelections && (
                <div className="error-text">{error.filterSelections}</div>
              )}
            </div>
          )}
          {radioSelector === "name" && (
            <>
              <div className="metric-submenu-section">
                <MetricSubheader
                  title={t("Select which item(s) you want to measure")}
                  required={true}
                  tooltip={
                    `${t("Can't find what you are looking for? Try searching for entire")} \
                    ${t('words within the names of your Work Items.')}`
                  }
                  tooltipClass="ado-workitem-children-popup"
                />
                <SearchBoxComp
                  minLength="2"
                  wrapperClass="group-results-people"
                  url="integration/searchAzureWorkItems"
                  inputProps={{
                    placeholder: t("Search for an epic, user story, task, etc."),
                    className: "font14"
                  }}
                  params={{
                    project: selectedProject.name,
                    instance_url: selectedInstance.name
                  }}
                  renderResultComp={renderDropdownMenu}
                  containerClassName="search-dropdown borderbox"
                />
                {error.filterSelections && (
                  <div className="error-text">{error.filterSelections}</div>
                )}
              </div>
              {!!Object.values(filterSelections).length && (
                <div className="metric-submenu-section">
                  <MetricSubheader title="Selected Items" />
                  <ADOWorkItemsList
                    items={Object.values(filterSelections)}
                    deleteItem={deleteWorkItemByName}
                  />
                </div>
              )}
            </>
          )}
          {radioSelector === "query" && (
            <div className="metric-submenu-section">
              <MetricSubheader
                title={t("Select which query you want to measure")}
                required={true}
              />
              <SingleSelectSearch
                placeholder={t("Search and select a query")}
                defaultSearchText={
                  filterSelections ? filterSelections.name : ""
                }
                items={[]}
                filter={filterQueries}
                minSearchLength={2}
                updateParentState={setFilterSelections}
              />
              {error.filterSelections && (
                <div className="error-text">{error.filterSelections}</div>
              )}
            </div>
          )}

          {radioSelector !== "query" && ( // Don't allow children to be searched for queries
            <div className="metric-submenu-section">
              <MetricSubheader
                title={t("How would you like to capture progress made on the specified Work Items?")}
                required={true}
                tooltip={
                  `${t('Work Items in Azure DevOps can have linked children,')} \
              ${t('which you can choose to include in your progress calculation.')}`
                }
                tooltipClass="ado-workitem-children-popup"
              />
              <MetricRadioGroup
                radioSelector={calculationType}
                setRadioSelector={setCalculationType}
                items={[
                  {
                    id: "level_0",
                    label: t("Only the specified Work Items")
                  },
                  {
                    id: "level_2",
                    label: `${t('Progress by All Work Items (Rollup)')}`
                  },
                  {
                    id: "level_1",
                    label: `${t('Selected Work Items and any children')}`
                  }
                ]}
                radioGroupName="SetFirst"
                subclassDefined="radioMarginClass"
              />
            </div>
          )}

          <div className="metric-submenu-section">
            <MetricSubheader
              title={t("What state do you want to measure as done?")}
              required={true}
            />
            <MetricCheckboxGroup
              checkboxes={doneMetrics}
              setCheckboxes={setDoneMetricsHandler}
              items={itemStates}
              checkboxName="DoneMetrics"
              subclassDefined="radioMarginClass"
            />
            {error.doneMetrics && (
              <div className="error-text">{error.doneMetrics}</div>
            )}
          </div>

          <div className="metric-submenu-section">
            <MetricSubheader title={t("Measure progress as")} required={true} />
            <MetricRadioGroup
              radioSelector={progress}
              setRadioSelector={setProgress}
              items={[
                { id: "percentage", label: t("Percentage complete") },
                { id: "quantity", label: t("Quantity complete") }
              ]}
              radioGroupName="SetSecond"
              subclassDefined="radioMarginClass"
            />
          </div>
          <MetricConsiderProgress
            considerProgress={considerProgress == "0" ? true : false}
            setConsiderProgress={e =>
              setConsiderProgress(e.currentTarget.checked ? "0" : "1")
            }
          />
          {    
            (props.addOkrData.display_nested_kr && 
            !props.isChildKr && !props.mitChildKr &&
            Math.ceil(moment(props.goalDetails.target_date).diff(moment(props.goalDetails.start_date), "days", true)) >= 360) &&
            <NestedInlineComp
            {...props}
            actions = {props.actions}
            nested_krs = {props.nested_krs}
            updateNestedKrs = {props.updateNestedKrs}
            />
          }
          <AddFinancialTargetsButton objectType="kr" objectId={props.item?.id} ownerUserId={props.realOwner?.id} />
          {!props.mitChildKr && <KrRelationshipWrapper {...props } metric={props.item} saveKr={saveKr}/>}
          {!props.mitChildKr && <AddKrTag metric={props.item} goalId={props.goalDetails.id} taghandler={saveTag}/> }
          {!props.isChildKr && !props.mitChildKr && 
            <KrAlignDependance {...props}
            saveDependentArr={setDependenciesData}
            saveAlignArr={setAlignData}
            krAlingArr={props.item.kr_aligns}
            krDependenciesArr={props.item.kr_dependent} />
          }
         
          {props.goalDetails && props.goalDetails.all_metrics == "0" && !props.mitChildKr && (
            <MetricPermissions
              item={props.item}
              goalDetails={props.goalDetails}
              setTeamPermissions={setTeamPermissions}
              setPermissions={setPermissions}
            />
          )}
        </>
      )}

      <div className="gray-error">{error.instances}</div>
      <div className="gray-error">{error.projects}</div>
      {showSavePopup
        && <RetainUpdateModal
          selectedType={selectedType}
          changeRadio={(value) => {
            setSelectedType(value);
          }}
          exitDeleteModal={() => {
            setShowSavePopup(false);
          }}
          deleteItems={() => {
            setShowSavePopup(false);
            handleSave(true);
          }}
          titleRetain={t('Save key result with new data type')}
          contentRetain={t('Are you sure you want to save this key result with the new data type selected?')}
        /> }
    </div>
  );
});

export default AdoPostLogin;
